/**
 * @copyright 2012-2025 Jarosław Foksa
 */

class ChildFrameBridge {
  #messageCounter = 0;
  #messageRequestCallbacks = {};
  #messageResponseCallbacks = {};

  #childFrame;
  #childFrameLoadedCallbacks = [];

  get #whenChildFrameLoaded() {
    return new Promise((resolve) => {
      // contentWindow is null while the <iframe> is disconnected from the DOM tree
      if (this.#childFrame.contentWindow === null) {
        this.#childFrameLoadedCallbacks.push(resolve);
      }
      else {
        resolve();
      }
    });
  }

  constructor(childFrame) {
    this.#childFrame = childFrame;

    this.#childFrame.addEventListener("load", () => this.#onChildFrameLoaded());
    window.addEventListener("message", (event) => this.#onMessage(event));
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Post message to the child frame.
   *
   * @type {(name: string, arg: any, callback?: Function) => void}
   */
  postMessage(name, arg, callback) {
    if (arg === undefined) {
      arg = null;
    }

    let id = this.#messageCounter++;
    let message = {channel: "request", id, name, arg};

    if (callback) {
      this.#messageResponseCallbacks[message.id] = callback;
    }

    this.#whenChildFrameLoaded.then(() => {
      this.#childFrame.contentWindow.postMessage(message, "*");
    });
  }

  /**
   * Listen for the given message from the child frame.
   *
   * @type {(name: string, callback: Function) => void}
   */
  addMessageListener(name, callback) {
    if (!this.#messageRequestCallbacks[name]) {
      this.#messageRequestCallbacks[name] = [];
    }

    this.#messageRequestCallbacks[name].push(callback);
  }

  /**
   * Stop listening for the given message from the child frame.
   *
   * @type {(name: string, callback: Function) => void}
   */
  removeMessageListener(name, callback) {
    if (this.#messageRequestCallbacks[name]) {
      this.#messageRequestCallbacks[name] = this.#messageRequestCallbacks[name].filter(($0) => $0 !== callback);
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  #onChildFrameLoaded() {
    for (let callback of this.#childFrameLoadedCallbacks) {
      callback();
    }

    this.#childFrameLoadedCallbacks = [];
  }

  #onMessage(event) {
    // Received message from the child frame
    if (event.source !== null && event.source === this.#childFrame.contentWindow) {
      let message = event.data;

      if (message.channel === "request") {
        if (this.#messageRequestCallbacks[message.name]) {
          this.#messageRequestCallbacks[message.name].forEach((callback) => {
            callback(message.arg, (response) => {
              let responseMessage = {channel: "response", id: message.id, name: message.name, arg: response};
              this.#childFrame.contentWindow.postMessage(responseMessage, "*");
            });
          });
        }
      }
      else if (message.channel === "response") {
        if (this.#messageResponseCallbacks[message.id]) {
          this.#messageResponseCallbacks[message.id](message.arg);
          delete this.#messageResponseCallbacks[message.id];
        }
      }
    }
  }
}

/**
 * Template string tag used to parse CSS strings.
 *
 * @type {(strings: TemplateStringsArray, ...expressions: Array<string>) => CSSStyleSheet}
 */
let css = (strings, ...expressions) => {
  let parts = [];

  for (let i = 0; i < strings.length; i += 1) {
    parts.push(strings[i]);
    if (expressions[i] !== undefined) parts.push(expressions[i]);
  }

  let cssText = parts.join("");
  let stylesheet = new CSSStyleSheet();
  stylesheet.replaceSync(cssText);
  return stylesheet;
};

/**
 * @type {(url: string) => boolean}
 */
let isValidURL = (url) => {
  try {
    return Boolean(new URL(url));
  }
  catch (_error) {
    return false;
  }
};

/**
 * @copyright 2012-2025 Jarosław Foksa
 */

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
 */
const MIME_TYPES = {
  // Images
  "png":   "image/png",
  "jpeg":  "image/jpeg",
  "jpg":   "image/jpeg",
  "gif":   "image/gif",
  "webp":  "image/webp",
  "avif":  "image/avif",
  "tiff":  "image/tiff",
  "svg":   "image/svg+xml",
  "svgz":  "image/svg+xml",
  // Fonts
  "ttf":   "font/ttf",
  "woff":  "font/woff",
  "woff2": "font/woff2",
  "otf":   "font/otf",
  // Other
  "pdf":   "application/pdf",
  "html":  "text/html",
  "css":   "text/css",
  "js":    "text/javascript",
  "json":  "application/json",
  "ftl":   "text/plain"
};

/**
 * Short IDs of tools that are shown by default.
 */
const DEFAULT_TOOLS = [
  "blob",
  "edit",
  "ellipse",
  "freehand",
  "other-shape",
  "pan",
  "rect",
  "scissors",
  "spline",
  "text",
  "text-path",
  "transform",
  "view",
  "zoom"
];

/**
 * Short IDs of panels that are shown by default.
 */
const DEFAULT_PANELS = [
  "animations",
  // "code",
  "compositing",
  "defs",
  "elements",
  "export",
  "fill",
  "generators",
  "geometry",
  "history",
  "libraries",
  "metadata",
  "objects",
  "stroke",
  "typography"
];

const DEFAULT_THEMES = [
  "fluent",
  "fluent-dark",
  "cupertino",
  "cupertino-dark",
  "adwaita",
  "adwaita-dark",
  "material",
  "material-dark"
];

var version = "4.104.0";
var locales = [
	"bg",
	"cs",
	"da",
	"de",
	"el",
	"en",
	"es",
	"fi",
	"fr",
	"hr",
	"it",
	"ja",
	"ko",
	"nl",
	"pl",
	"pt",
	"ro",
	"sv",
	"tr",
	"uk",
	"zh"
];
var MANIFEST = {
	version: version,
	locales: locales};

const DEFAULT_NAME             = "Boxy SVG";
const DEFAULT_SRC_URL          = import.meta.resolve("./default.svg");
const DEFAULT_LOGO_URL         = import.meta.resolve("./logo.svg");
const DEFAULT_CREDITS_URL      = import.meta.resolve("./credits.html");
const DEFAULT_MENUS_URL        = import.meta.resolve("./menus.html");
const DEFAULT_LOCALES_URL      = import.meta.resolve("./locales/");
const DEFAULT_THEMES_URL       = import.meta.resolve("./themes/");
const DEFAULT_ICONS_URL        = import.meta.resolve("./icons/default.svg");
const DEFUALT_LOADING          = "eager";
const IFRAME_URL               = import.meta.resolve("./app.html");

/**
 * @typedef {"blob" | "edit" | "ellipse" | "freehand" | "other-shape" | "pan" | "rectangle" | "scissors" |
 *           "spline" | "text" | "text-path" | "transform" | "view" | "zoom"} ToolID
 *
 * @typedef {"animations" | "compositing" | "defs" | "elements" | "export" | "fill" | "generators" | "geometry" |
 *           "history" | "libraries" | "metadata" | "objects" | "stroke" | "typography"} PanelID
 *
 * @typedef {{
 *   removeBoxySVGMetadata?:              boolean,
 *   removeForeignMetadata?:              boolean,
 *   removeAnimations?:                   boolean,
 *   resetTransformOrigins?:              boolean,
 *   convertTextsToPaths?:                boolean,
 *   convertStopColorAlphaToStopOpacity?: boolean,
 *   embedExternalFonts?:                 boolean,
 *   splitCompoundPaths?:                 boolean
 * }} SVGNormalizationOptions
 *
 * @typedef {"web-static" | "web-secure-static" | "web-animated" | "web-secure-animated" |
 *           "web-dynamic-interactive" | "boxy-svg"} SVGNormalizationPreset
 *
 * @typedef {PNGFileOptions | JPEGFileOptions | WebPFileOptions | GifFileOptions | SVGFileOptions |
 *           HTMLFileOptions | PDFFileOptions} FileOptions
 *
 * @typedef {{
 *   format:        "png",
 *   href?:         string,
 *   width?:        "auto" | number,
 *   height?:       "auto" | number,
 *   scale?:        number,
 *   dpi?:          number,
 *   background?:   string,
 *   antiAliasing?: boolean,
 *   animation?:    null | number | AnimationOptions
 * }} PNGFileOptions
 *
 * @typedef {{
 *   format:        "jpeg",
 *   href?:         string,
 *   width?:        "auto" | number,
 *   height?:       "auto" | number,
 *   scale?:        number,
 *   dpi?:          number,
 *   compression?:  number,
 *   background?:   string,
 *   antiAliasing?: boolean,
 *   progressive?:  boolean,
 *   animation?:    null | number
 * }} JPEGFileOptions
 *
 * @typedef {{
 *   format:        "webp",
 *   href?:         string,
 *   width?:        "auto" | number,
 *   height?:       "auto" | number,
 *   scale?:        number,
 *   compression?:  {value: number, lossy: boolean},
 *   background?:   string,
 *   antiAliasing?: boolean,
 *   animation?:    null | number | AnimationOptions
 * }} WebPFileOptions
 *
 * @typedef {{
 *   format:        "gif",
 *   href?:         string,
 *   width?:        "auto" | number,
 *   height?:       "auto" | number,
 *   scale?:        number,
 *   background?:   string,
 *   antiAliasing?: boolean,
 *   dithering?:    string,
 *   animation?:    null | number | AnimationOptions
 * }} GifFileOptions
 *
 * @typedef {{
 *   format:        "svg",
 *   href?:         string,
 *   width?:        "auto" | null | number,
 *   height?:       "auto" | null | number,
 *   units?:        "px" | "cm" | "mm" | "in" | "pt" | "pc" | "q" | "%",
 *   preserveAspectRatio?: boolean,
 *   compression?:  boolean,
 *   normalization?: null | SVGNormalizationPreset | SVGNormalizationOptions
 * }} SVGFileOptions
 *
 * @typedef {{
 *   format:        "html",
 *   href?:         string
 * }} HTMLFileOptions
 *
 * @typedef {{
 *   format:        "pdf",
 *   pages?:        Array<PDFPageOptions>
 * }} PDFFileOptions
 *
 * @typedef {{
 *   href?:         string,
 *   width?:        "auto" | number,
 *   height?:       "auto" | number,
 *   units?:        "cm" | "in",
 *   background?:   string,
 *   animation?:    null | number
 * }} PDFPageOptions
 *
 * @typedef {{
 *   begin:         number,
 *   end:           number,
 *   fps:           number,
 *   loop:          boolean
 * }} AnimationOptions
 *
 * @typedef {{
 *   id:            string,
 *   type:          "colors" | "gradients" | "bitmaps" | "vectors",
 *   label:         string,
 *   icon:          null | string,
 *   search: (query: string, page: number) => Promise<Array<{source:string, thumbnail?:string, details?:string}>>
 * }} LibraryOptions
 */

/**
 * Implements `<boxy-svg>` custom element.
 *
 * @fires change
 * @fires configchange
 * @fires zoomchange
 * @fires menubarstatechange
 * @fires command
 * @fires drop
 * @fires export
 *
 * @see https://boxy-svg.com/component
 */
class BoxySVGElement extends HTMLElement {
  static observedAttributes = [
    "src",
    "name",
    "version",
    "logo",
    "credits",
    "menus",
    "locales",
    "themes",
    "icons",
    "accentcolor",
    "loading",
    "tools",
    "panels"
  ];

  static #shadowStyleSheet = css`
    :host {
      display: block;
      width: 100%;
      height: 600px;
      min-height: 1px;
      box-sizing: border-box;
      border: 1px solid #999999;
      contain: strict;
    }
    :host([hidden]) {
      display: none;
    }
    :host(:focus) {
      outline: none;
    }

    :host([theme*="fluent"])         { border-color: #999999; }
    :host([theme*="fluent-dark"])    { border-color: #666666; }
    :host([theme*="cupertino"])      { border-color: #b0b0b0; }
    :host([theme*="cupertino-dark"]) { border-color: #747474; }
    :host([theme*="adwaita"])        { border-color: #d1d1d1; }
    :host([theme*="adwaita-dark"])   { border-color: #525252; }

    #iframe {
      display: block;
      box-sizing: border-box;
      width: 100%;
      height: 100%;
      min-height: inherit;
      margin: 0;
      padding: 0;
      border: none;
      user-select: none;
      contain: strict;
      -webkit-user-select: none;
    }
    #iframe[hidden] {
      visibility: hidden;
    }
  `;

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * URL of the edited SVG file.
   *
   * For files loaded from disk using the "open" command or from string using the `setSVG()` method the value is
   * `null` .
   *
   * @type {string | null}
   * @default "/node_modules/@boxy-svg/boxy-svg/default.svg"
   * @attribute
   */
  get src() {
    let src = this.hasAttribute("src") ? this.getAttribute("src") : DEFAULT_SRC_URL;
    return this.#normalizeURL(src);
  }
  set src(src) {
    this.setAttribute("src", src);
  }

  /**
   * The app name to be shown by the "About" dialog.
   *
   * @type {string}
   * @default "Boxy SVG"
   * @attribute
   */
  get name() {
    return this.hasAttribute("name") ? this.getAttribute("name") : DEFAULT_NAME;
  }
  set name(name) {
    this.setAttribute("name", name);
  }

  /**
   * The app version string to be shown by the "About" dialog.
   *
   * @type {string}
   * @default Same as `version` in `/node_modules/@boxy-svg/boxy-svg/package.json`
   * @attribute
   */
  get version() {
    return this.hasAttribute("version") ? this.getAttribute("version") : MANIFEST.version;
  }
  set version(version) {
    this.setAttribute("version", version);
  }

  /**
   * URL to the app logo in SVG format, to be shown by the "About" dialog.
   *
   * @type {string}
   * @default "/node_modules/@boxy-svg/boxy-svg/logo.svg"
   * @attribute
   */
  get logo() {
    let logo = this.hasAttribute("logo") ? this.getAttribute("logo") : DEFAULT_LOGO_URL;
    return this.#normalizeURL(logo);
  }
  set logo(logo) {
    this.setAttribute("logo", logo);
  }

  /**
   * URL to the app credits in HTML format, to be shown by the "About" dialog.
   *
   * @type {string}
   * @default "/node_modules/@boxy-svg/boxy-svg/credits.html"
   * @attribute
   */
  get credits() {
    let credits = this.hasAttribute("credits") ? this.getAttribute("credits") : DEFAULT_CREDITS_URL;
    return this.#normalizeURL(credits);
  }
  set credits(credits) {
    this.setAttribute("credits", credits);
  }

  /**
   * URL to the app menus in HTML format.
   *
   * @type {string}
   * @default "/node_modules/@boxy-svg/boxy-svg/menus.html"
   * @attribute
   */
  get menus() {
    let menus = this.hasAttribute("menus") ? this.getAttribute("menus") : DEFAULT_MENUS_URL;
    return this.#normalizeURL(menus);
  }
  set menus(menus) {
    this.setAttribute("menus", menus);
  }

  /**
   * URLs to UI localization files in [FTL format](https://projectfluent.org/fluent/guide/).
   *
   * Each file name should be an ISO 639 language code (e.g. "en", "es" or "de") followed by ".ftl" extension.
   *
   * If multiple files with the same language code are provided, subsequent files will be used as fallback when a
   * message is not found in the primary file.
   *
   * If multiple URLs with different language codes are provided, user will be able to switch between them via
   * "Settings -> UI -> Language".
   *
   * @type {Array<string>}
   * @default ["/node_modules/@boxy-svg/boxy-svg/locales/en.ftl", ...]
   * @attribute
   */
  get locales() {
    if (this.hasAttribute("locales") === false) {
      return MANIFEST.locales.map(locale => DEFAULT_LOCALES_URL + locale + ".ftl");
    }
    else {
      let urls = this.getAttribute("locales").trim().split(/[ ,]+/).map(url => this.#normalizeURL(url));
      return urls;
    }
  }
  set locales(value) {
    this.setAttribute("locales", value.join(", "));
  }

  /**
   * URLs to UI theme files in CSS format.
   *
   * If multiple URLs are provided, user will be able to switch between themes via "Settings -> UI -> Theme".
   *
   * Use `@import` rule in your theme file to extend an existing Boxy SVG theme, e.g.:
   * `@import url("/node_modules/@boxy-svg/boxy-svg/themes/fluent.css");`
   *
   * @type {Array<string>}
   * @default ["/node_modules/@boxy-svg/boxy-svg/themes/fluent.css", ...]
   * @attribute
   */
  get themes() {
    if (this.hasAttribute("themes") === false) {
      return DEFAULT_THEMES.map(theme => DEFAULT_THEMES_URL + theme + ".css");
    }
    else {
      let urls = this.getAttribute("themes").trim().split(/[ ,]+/).map(url => this.#normalizeURL(url));
      return urls;
    }
  }
  set themes(themes) {
    this.setAttribute("themes", themes.join(", "));
  }

  /**
   * URLs to iconset files in SVG format.
   *
   * If multiple URLs are provided, subsequent files will be used as a fallback when an icon is not found in the
   * primary file.
   *
   * @type {Array<string>}
   * @default ["/node_modules/@boxy-svg/boxy-svg/icons/default.svg"]
   * @attribute
   */
  get icons() {
    let attrValue = this.hasAttribute("icons") ? this.getAttribute("icons") : DEFAULT_ICONS_URL;
    let urls = attrValue.trim().split(/[ ,]+/).map((url) => this.#normalizeURL(url));
    return urls;
  }
  set icons(value) {
    this.setAttribute("icons", value.join(", "));
  }

  /**
   * UI accent color.
   *
   * You can use either a hex color code such as `"#bada55"` or one of the following preset color names:
   * `"blue"`, `"purple"`, `"pink"`, `"red"`, `"orange"`, `"yellow"`, `"green"`, `"graphite"`.
   *
   * @type {string | null}
   * @default null
   * @attribute
   */
  get accentColor() {
    if (this.hasAttribute("accentcolor")) {
      return this.getAttribute("accentcolor").trim();
    }
    else {
      return null;
    }
  }
  set accentColor(value) {
    if (value === null) {
      this.removeAttribute("accentcolor");
    }
    else {
      this.setAttribute("accentcolor", value);
    }
  }

  /**
   * Specifies whether the SVG editor should be loaded immediately (`"eager"`) or once it becomes visible on the
   * screen (`"lazy"`).
   *
   * @type {"eager" | "lazy"}
   * @default "eager"
   * @attribute
   */
  get loading() {
    let value = this.getAttribute("loading");

    if (["eager", "lazy"].includes(value)) {
      return /** @type {"eager" | "lazy"} */ (value);
    }
    else {
      return DEFUALT_LOADING;
    }
  }
  set loading(value) {
    this.setAttribute("loading", value);
  }

  /**
   * IDs of tools to be loaded.
   *
   * @type {Array<ToolID>}
   * @default DEFAULT_TOOLS
   * @attribute
   */
  get tools() {
    let tools;

    if (this.hasAttribute("tools")) {
      tools = this.getAttribute("tools").trim().split(/[ ,]+/);

      if (tools.includes("transform") === false) {
        tools.unshift("transform");
      }

      tools = tools.filter($0 => DEFAULT_TOOLS.includes($0));
    }
    else {
      tools = [...DEFAULT_TOOLS];
    }

    return /** @type {Array<ToolID>} */ (tools);
  }
  set tools(tools) {
    tools = tools.filter($0 => DEFAULT_TOOLS.includes($0));

    if (tools.length === 0) {
      this.removeAttribute("tools");
    }
    else {
      this.setAttribute("tools", tools.join(", "));
    }
  }

  /**
   * IDs of panels to be loaded.
   *
   * @type {Array<PanelID>}
   * @default DEFAULT_PANELS
   * @attribute
   */
  get panels() {
    let panels;

    if (this.hasAttribute("panels")) {
      panels = this.getAttribute("panels").trim().split(/[ ,]+/);

      if (panels.includes("fill") === false) {
        panels.unshift("fill");
      }

      panels = panels.filter($0 => DEFAULT_PANELS.includes($0));
    }
    else {
      panels = [...DEFAULT_PANELS];
    }

    return /** @type {Array<PanelID>} */ (panels);
  }
  set panels(panels) {
    panels = panels.filter($0 => DEFAULT_PANELS.includes($0));

    if (panels.length === 0) {
      this.removeAttribute("panels");
    }
    else {
      this.setAttribute("panels", panels.join(", "));
    }
  }

  /**
   * The current canvas zoom level.
   *
   * @type {number}
   * @default 100
   */
  get zoom() {
    return this.#zoom;
  }
  set zoom(zoom) {
    this.#zoom = zoom;
    this.#bridge.postMessage("setZoom", zoom);
  }

  /**
   * Storage area used to keep configuration options.
   *
   * @type {Storage}
   * @default localStorage
   */
  get configStorage() {
    return this.#configStorage;
  }
  set configStorage(storage) {
    if (this.#configStorage !== storage) {
      this.#configStorage = storage;
      this.#bridge.postMessage("onConfigStorageChange");

      // Special case: scale changes must be handled immediately
      this.#updateScale();
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Promise resolved when the component is fully loaded and initialized.
   *
   * @type {Promise<void>}
   */
  get whenReady() {
    return new Promise((resolve) => {
      if (this.#readyCallbacks === null) {
        resolve();
      }
      else {
        this.#readyCallbacks.push(resolve);
      }
    });
  }

  #shadowRoot;
  #bridge;
  #zoom = 100;
  #readyCallbacks = [];
  #configStorage = localStorage;

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor() {
    super();

    this.#shadowRoot = this.attachShadow({mode: "closed", delegatesFocus: true});
    this.#shadowRoot.adoptedStyleSheets = [BoxySVGElement.#shadowStyleSheet];

    this["#iframe"] = document.createElement("iframe");
    this["#iframe"].setAttribute("id", "iframe");
    this["#iframe"].setAttribute("loading", "lazy");
    this["#iframe"].setAttribute("hidden", "");
    this["#iframe"].setAttribute("src", IFRAME_URL);

    if (location.origin !== new URL(this["#iframe"].src).origin) {
      this["#iframe"].setAttribute("sandbox", "allow-scripts allow-modals allow-popups allow-downloads allow-same-origin");
    }
    if (location.protocol !== "electron:") {
      this["#iframe"].setAttribute("allow", "clipboard-read; clipboard-write; local-fonts; ch-prefers-color-scheme");
    }

    this.#shadowRoot.append(this["#iframe"]);

    this.#bridge = new ChildFrameBridge(this["#iframe"]);

    this.#bridge.addMessageListener("beforeready", async (_arg, callback) => {
      let {name, version, accentColor, tools, panels} = this;

      let src         = await this.#prefetchSettingValue("src", this.src);
      let logo        = await this.#prefetchSettingValue("logo", this.logo);
      let credits     = await this.#prefetchSettingValue("credits", this.credits);
      let menus       = await this.#prefetchSettingValue("menus", this.menus);
      let locales     = await this.#prefetchSettingValue("locales", this.locales);
      let themes      = await this.#prefetchSettingValue("themes", this.themes);
      let icons       = await this.#prefetchSettingValue("icons", this.icons);

      callback({name, version, accentColor, tools, panels, src, logo, credits, menus, locales, themes, icons});
    });

    this.#bridge.addMessageListener("ready", () => {
      if (this.#readyCallbacks !== null) {
        for (let callback of this.#readyCallbacks) {
          callback();
        }

        this.#readyCallbacks = null;
      }

      this["#iframe"].hidden = false;
    });

    this.#bridge.addMessageListener("command", (commandID, callback) => {
      let event = new CustomEvent("command", {detail: commandID, cancelable: true});
      this.dispatchEvent(event);
      callback(event.defaultPrevented);
    });

    this.#bridge.addMessageListener("drop", (files, callback) => {
      let event = new CustomEvent("drop", {detail: files, cancelable: true});
      this.dispatchEvent(event);
      callback(event.defaultPrevented);
    });

    this.#bridge.addMessageListener("export", (files, callback) => {
      let event = new CustomEvent("export", {detail: files, cancelable: true});
      this.dispatchEvent(event);
      callback(event.defaultPrevented);
    });

    this.#bridge.addMessageListener("printToPDF", async (printableHTML, callback) => {
      let data = await this.printToPDF(printableHTML);
      callback(data);
    });

    this.#bridge.addMessageListener("change", (change) => {
      this.dispatchEvent(
        new CustomEvent("change", {detail: change})
      );
    });

    this.#bridge.addMessageListener("configchange", ([key, value]) => {
      if (value === null) {
        this.#configStorage.removeItem(key);
      }
      else {
        this.#configStorage.setItem(key, JSON.stringify(value));
      }

      if (key === "bx-uisettings:scale") {
        this.#updateScale();
      }

      this.dispatchEvent(
        new CustomEvent("configchange", {detail: {key, value}})
      );
    });

    this.#bridge.addMessageListener("zoomchange", (value) => {
      this.#zoom = value;

      this.dispatchEvent(
        new CustomEvent("zoomchange", {detail: value})
      );
    });

    this.#bridge.addMessageListener("menubarstatechange", (state) => {
      this.dispatchEvent(
        new CustomEvent("menubarstatechange", {detail: state})
      );
    });

    this.#bridge.addMessageListener("getConfig", (key, callback) => {
      callback(this.getConfig(key));
    });

    // Fired when localStorage or sessionStorage is changed in another tab, window or iframe with the same origin
    window.addEventListener("storage", (event) => {
      if (event.storageArea === this.#configStorage) {
        let key = event.key;

        if (key?.startsWith("bx-") || key?.startsWith("x-")) {
          let value = (event.newValue === null) ? null : JSON.parse(event.newValue);

          this.#bridge.postMessage("setConfig", [key, value]);
          this.dispatchEvent(new CustomEvent("configchange", {detail: {key, value}}));
        }
      }
    });
  }

  connectedCallback() {
    if (this.hasAttribute("loading") === false) {
      this["#iframe"].setAttribute("loading", "eager");
    }
    if (this.hasAttribute("tabindex") === false) {
      this.setAttribute("tabindex", "0");
    }

    this.#updateScale();
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || this.#readyCallbacks !== null) {
      return;
    }
    else {
      await this.whenReady;

      if (name === "accentcolor") {
        name = "accentColor";
      }

      if (["name", "version", "accentColor", "tools", "panels"].includes(name)) {
        let value = this[name];
        this.#bridge.postMessage("onComponentSettingsChange", [name, value]);
      }
      else if (["src", "logo", "credits", "menus", "locales", "themes", "icons"].includes(name)) {
        let value = await this.#prefetchSettingValue(name, this[name]);
        this.#bridge.postMessage("onComponentSettingsChange", [name, value]);
      }
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Get the currently edited SVG document.
   *
   * @type {(normalization?: SVGNormalizationOptions | SVGNormalizationPreset) => Promise<string>}
   */
  getSVG(normalization = {}) {
    return new Promise(async (resolve) => {
      await this.whenReady;

      this.#bridge.postMessage("getSVG", normalization, (svg) => {
        resolve(svg);
      });
    });
  }

  /**
   * Set the currently edited SVG document.
   *
   * @type {(svg: string) => Promise<void>}
   */
  setSVG(svg) {
    return new Promise(async (resolve) => {
      await this.whenReady;

      this.#bridge.postMessage("setSVG", svg, () => {
        resolve();
      });
    });
  }

  /**
   * Same as `setSVG()`, but also accepts bitmap and PDF data (passed as base64-encoded data: URL string).
   *
   * @type {(data: string) => Promise<void>}
   */
  setData(data) {
    return new Promise(async (resolve) => {
      await this.whenReady;

      this.#bridge.postMessage("setData", data, () => {
        resolve();
      });
    });
  }

  /**
   * Insert another SVG document into the currently edited SVG document.
   *
   * @type {(svg: string) => Promise<void>}
   */
  insertSVG(svg) {
    return new Promise(async (resolve) => {
      await this.whenReady;

      this.#bridge.postMessage("insertSVG", svg, () => {
        resolve();
      });
    });
  }

  /**
   * Get the advanced configuration option.
   *
   * @type {(key?: string | null, defaultValue?: any) => any}
   */
  getConfig(key = null, defaultValue = null) {
    if (key === null) {
      let config = {};

      for (let i = 0; i < this.#configStorage.length; i += 1) {
        let currentKey = this.#configStorage.key(i);

        if (currentKey.startsWith("bx-") || currentKey.startsWith("x-")) {
          let rawValue = this.#configStorage.getItem(currentKey);
          let value = (rawValue === null) ? rawValue : JSON.parse(rawValue);
          config[currentKey] = value;
        }
      }

      return config;
    }
    else {
      let rawValue = null;

      if (key.startsWith("bx-") || key.startsWith("x-")) {
        rawValue = this.#configStorage.getItem(key);
      }

      let value = (rawValue === null) ? rawValue : JSON.parse(rawValue);

      if (value === null) {
        return defaultValue;
      }
      else {
        return value;
      }
    }
  }

  /**
   * Set the advanced configuration option.
   *
   * @type {(key: string, value: any) => void}
   */
  setConfig(key, value) {
    if (key?.startsWith("bx-") || key?.startsWith("x-")) {
      if (value === null) {
        this.#configStorage.removeItem(key);
      }
      else {
        this.#configStorage.setItem(key, JSON.stringify(value));
      }

      // Special case: scale changes must be handled immediately
      if (key === "bx-uisettings:scale") {
        this.#updateScale();
      }

      this.whenReady.then(() => {
        this.#bridge.postMessage("setConfig", [key, value]);
      });
    }
  }

  /**
   * Get IDs of all available commands.
   *
   * @type {() => Promise<Array<string>>}
   */
  getCommands() {
    return new Promise(async (resolve) => {
      await this.whenReady;

      this.#bridge.postMessage("getCommands", null, (commands) => {
        resolve(commands);
      });
    });
  }

  /**
   * Execute the command with the given ID.
   *
   * @type {(commandID: string) => Promise<boolean>}
   */
  execCommand(commandID) {
    return new Promise(async (resolve) => {
      await this.whenReady;

      this.#bridge.postMessage("execCommand", commandID, (executed) => {
        resolve(executed);
      });
    });
  }

  /**
   * Export the currently edited SVG document into one or more files.
   *
   * @type {(options: Array<FileOptions>) => Promise<Array<Blob>>}
   */
  export(options) {
    return new Promise(async (resolve) => {
      await this.whenReady;

      let blobs = [];

      this.#bridge.postMessage("export", options, (buffers) => {
        for (let i = 0; i < options.length; i += 1) {
          let {format} = options[i];
          let buffer = buffers[i];
          let blob = new Blob([buffer], {type: MIME_TYPES[format]});
          blobs.push(blob);
        }

        resolve(blobs);
      });
    });
  }

  #libraries = {};

  /**
   * Add library to the "Libraries" panel.
   *
   * @type {(options: LibraryOptions) => Promise<void>}
   */
  addLibrary(options) {
    return new Promise(async (resolve, reject) => {
      await this.whenReady;

      if (!options.id) {
        return reject(`Library options must have an "id" property`);
      }
      if (this.#libraries[options.id] !== undefined) {
        console.info(`Library with ID "${options.id}" already added`);
        return resolve();
      }

      let prefetch = (src) => {
        return new Promise(async (resolve) => {
          if (isValidURL(src) === false) {
            return resolve(src);
          }
          else {
            let srcURL = new URL(src);

            if (
              this.#isTrustedURL(srcURL.origin) ||
              srcURL.origin === "https://images.unsplash.com" ||
              srcURL.protocol === "data:" || srcURL.origin === new URL(this["#iframe"].src).origin
            ) {
              return resolve(src);
            }
            else {
              let result = await (await fetch(src)).text();
              resolve(result);
            }
          }
        });
      };

      this.#libraries[options.id] = async ({query, page}, callback) => {
        let results = await options.search(query, page);

        for (let result of results) {
          result.source    = await prefetch(result.source);
          result.thumbnail = await prefetch(result.thumbnail);
        }

        callback(results);
      };

      options.icon = await prefetch(options.icon);

      this.#bridge.postMessage("addLibrary", {...options, search: null}, () => resolve());
      this.#bridge.addMessageListener(`searchLibrary:${options.id}`, this.#libraries[options.id]);

      resolve();
    });
  }

  /**
   * Remove a library from the "Libraries" panel.
   *
   * @type {(libraryID: string) => Promise<void>}
   */
  removeLibrary(libraryID) {
    return new Promise(async (resolve) => {
      await this.whenReady;

      this.#bridge.postMessage("removeLibrary", libraryID);

      if (this.#libraries[libraryID]) {
        this.#bridge.removeMessageListener(`searchLibrary:${libraryID}`, this.#libraries[libraryID]);
        delete this.#libraries[libraryID];
      }

      resolve();
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Electron-based apps override this method.
   *
   * @type {(data: string) => Promise<Uint8Array | null>}
   */
  printToPDF(_data) {
    return new Promise((resolve) => {
      resolve(null);
    });
  }

  /**
   * Demo app on boxy-svg.com uses this method to toggle tools and panels and select objects.
   *
   * @type {(toolID: string, panelID: string, selector: string) => void}
   */
  _setDemoState(toolID, panelID, selector) {
    return new Promise(async (resolve) => {
      await this.whenReady;

      this.#bridge.postMessage("setDemoState", [toolID, panelID, selector], () => {
        resolve();
      });
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  #updateScale() {
    let scale = this.getConfig("bx-uisettings:scale", 1);

    if (scale === 0) {
      this.style.zoom = null;
    }
    else {
      this.style.zoom = `${scale}`;
    }
  }

  #prefetchSettingValue(name, value) {
    return new Promise(async (resolve) => {
      let prefetchedValue = value;

      if (value) {
        if (["src", "logo", "credits", "menus"].includes(name)) {
          if (this.#isTrustedURL(value) === false) {
            prefetchedValue = await (await fetch(value)).text();
          }
        }
        else if (name === "locales") {
          prefetchedValue = {};

          for (let url of value) {
            let fileName = url.substring(url.lastIndexOf("/") + 1);
            let lang = fileName.substring(0, fileName.indexOf("."));
            let src = url;

            if (this.#isTrustedURL(url) === false) {
              src = await (await fetch(url)).text();
            }

            if (prefetchedValue[lang] === undefined) {
              prefetchedValue[lang] = [];
            }

            prefetchedValue[lang].push(src);
          }
        }
        else if (name === "themes") {
          prefetchedValue = {};

          for (let url of value) {
            let fileName = url.substring(url.lastIndexOf("/") + 1);
            let id = fileName.substring(0, fileName.indexOf("."));
            let src = url;

            if (this.#isTrustedURL(url) === false) {
              src = await (await fetch(url)).text();
            }

            prefetchedValue[id] = src;
          }
        }
        else if (name === "icons") {
          prefetchedValue = [...value];

          for (let i = 0; i < prefetchedValue.length; i += 1) {
            if (this.#isTrustedURL(prefetchedValue[i]) === false) {
              prefetchedValue[i] = await (await fetch(prefetchedValue[i])).text();
            }
          }
        }
      }

      resolve(prefetchedValue);
    });
  }

  #normalizeURL(url) {
    if (
      url.startsWith("http") === false &&
      url.startsWith("data:") === false &&
      url.startsWith("blob:") === false &&
      url.startsWith("electron:") === false
    ) {
      // Electron
      if (location.protocol === "file:" || location.protocol === "electron:") {
        url = (url.startsWith("/") ? "" : "/") + url;
      }
      // Web
      else {
        url = location.origin + (url.startsWith("/") ? "" : "/") + url;
      }
    }

    return url;
  }

  #isTrustedURL(url) {
    if (url.startsWith("http")) {
      let componentURL = new URL(import.meta.url);
      return (location.origin === componentURL.origin && location.protocol === componentURL.protocol);
    }
    else if (url.startsWith("data:")) {
      return true;
    }
    // "blob:", "electron:", etc
    else {
      return false;
    }
  }
}

if (customElements.get("boxy-svg") === undefined) {
  customElements.define("boxy-svg", BoxySVGElement);
}

export { BoxySVGElement as default };
